import { X, Filter, Check, ChevronDown } from "lucide-react";
import { useState } from "react";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "../../ui/dropdown-menu";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Badge } from "../../ui/badge";

interface StoreFilterProps {
  storeOptions: string[];
  rankingOptions: string[];
  criteriaOptions: string[];
  windowSize: number;
  setWindowSize: (n: number) => void;
  selectedStores: string[];
  setSelectedStores: (stores: string[]) => void;
  selectedRankings: string[];
  setSelectedRankings: (ranks: string[]) => void;
  selectedCriteria: string[];
  setSelectedCriteria: (criteria: string[]) => void;
  onApply?: () => void;
  onReset?: () => void;
  onClose?: () => void;
  isOpen?: boolean;
}

export function WholesaleStoreFilter({
  storeOptions,
  rankingOptions,
  criteriaOptions,
  windowSize,
  setWindowSize,
  selectedStores,
  setSelectedStores,
  selectedRankings,
  setSelectedRankings,
  selectedCriteria,
  setSelectedCriteria,
  onApply,
  onReset,
  onClose,
  isOpen = true,
}: StoreFilterProps) {
  const [searchTerms, setSearchTerms] = useState({
    stores: "",
    rankings: "",
    criteria: "",
  });

  const toggleSelect = (arr: string[], setter: (arr: string[]) => void, value: string) => {
    setter(arr.includes(value) ? arr.filter(v => v !== value) : [...arr, value]);
  };

  const selectAll = (options: string[], setter: (arr: string[]) => void) => {
    setter(options);
  };

  const clearAll = (setter: (arr: string[]) => void) => {
    setter([]);
  };

  const getFilteredOptions = (options: string[], searchTerm: string) => {
    return options.filter(option =>
      option.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const handleReset = () => {
    setSelectedStores([]);
    setSelectedRankings([]);
    setSelectedCriteria([]);
    setWindowSize(30);
    setSearchTerms({ stores: "", rankings: "", criteria: "" });
    onReset?.();
  };

  const getActiveFiltersCount = () => {
    return selectedStores.length + selectedRankings.length + selectedCriteria.length;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-background border border-border rounded-lg shadow-lg w-full max-w-2xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div className="flex items-center gap-2">
            <Filter className="h-5 w-5 text-primary" />
            <h2 className="text-lg font-semibold">Advance Filter Options</h2>
            {getActiveFiltersCount() > 0 && (
              <Badge variant="secondary" className="ml-2">
                {getActiveFiltersCount()} active
              </Badge>
            )}
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="h-8 w-8 p-0"
            aria-label="Close filter"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-6 max-h-[calc(90vh-140px)] overflow-y-auto">
          {/* Store Filter */}
          <div className="space-y-3">
            <Label className="text-sm font-medium">Stores</Label>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  className="w-full justify-between min-h-10"
                >
                  <span className="truncate">
                    {selectedStores.length > 0
                      ? `${selectedStores.length} store${selectedStores.length !== 1 ? 's' : ''} selected`
                      : "Select stores"}
                  </span>
                  <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-80 p-0">
                <div className="p-2 border-b">
                  <Input
                    placeholder="Search stores..."
                    value={searchTerms.stores}
                    onChange={(e) => setSearchTerms(prev => ({ ...prev, stores: e.target.value }))}
                    className="h-8"
                  />
                </div>
                <div className="p-2 border-b flex gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 text-xs"
                    onClick={() => selectAll(storeOptions, setSelectedStores)}
                  >
                    Select All
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 text-xs"
                    onClick={() => clearAll(setSelectedStores)}
                  >
                    Clear All
                  </Button>
                </div>
                <div className="max-h-48 overflow-y-auto">
                  {getFilteredOptions(storeOptions, searchTerms.stores).map(store => (
                    <DropdownMenuCheckboxItem
                      key={store}
                      checked={selectedStores.includes(store)}
                      onCheckedChange={() => toggleSelect(selectedStores, setSelectedStores, store)}
                      className="pl-8"
                    >
                      {store}
                    </DropdownMenuCheckboxItem>
                  ))}
                  {getFilteredOptions(storeOptions, searchTerms.stores).length === 0 && (
                    <div className="py-2 px-4 text-sm text-muted-foreground">No stores found</div>
                  )}
                </div>
              </DropdownMenuContent>
            </DropdownMenu>
            {selectedStores.length > 0 && (
              <div className="flex flex-wrap gap-1 max-h-20 overflow-y-auto">
                {selectedStores.map(store => (
                  <Badge key={store} variant="secondary" className="text-xs">
                    {store}
                    <button
                      type="button"
                      className="ml-1 hover:text-destructive"
                      onClick={() => setSelectedStores(selectedStores.filter(s => s !== store))}
                      aria-label={`Remove ${store}`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
          </div>

          {/* Rankings Filter */}
          <div className="space-y-3">
            <Label className="text-sm font-medium">Rankings</Label>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  className="w-full justify-between min-h-10"
                >
                  <span className="truncate">
                    {selectedRankings.length > 0
                      ? `${selectedRankings.length} ranking${selectedRankings.length !== 1 ? 's' : ''} selected`
                      : "Select rankings"}
                  </span>
                  <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-80 p-0">
                <div className="p-2 border-b">
                  <Input
                    placeholder="Search rankings..."
                    value={searchTerms.rankings}
                    onChange={(e) => setSearchTerms(prev => ({ ...prev, rankings: e.target.value }))}
                    className="h-8"
                  />
                </div>
                <div className="p-2 border-b flex gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 text-xs"
                    onClick={() => selectAll(rankingOptions, setSelectedRankings)}
                  >
                    Select All
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 text-xs"
                    onClick={() => clearAll(setSelectedRankings)}
                  >
                    Clear All
                  </Button>
                </div>
                <div className="max-h-48 overflow-y-auto">
                  {getFilteredOptions(rankingOptions, searchTerms.rankings).map(rank => (
                    <DropdownMenuCheckboxItem
                      key={rank}
                      checked={selectedRankings.includes(rank)}
                      onCheckedChange={() => toggleSelect(selectedRankings, setSelectedRankings, rank)}
                      className="pl-8"
                    >
                      {rank}
                    </DropdownMenuCheckboxItem>
                  ))}
                  {getFilteredOptions(rankingOptions, searchTerms.rankings).length === 0 && (
                    <div className="py-2 px-4 text-sm text-muted-foreground">No rankings found</div>
                  )}
                </div>
              </DropdownMenuContent>
            </DropdownMenu>
            {selectedRankings.length > 0 && (
              <div className="flex flex-wrap gap-1 max-h-20 overflow-y-auto">
                {selectedRankings.map(rank => (
                  <Badge key={rank} variant="secondary" className="text-xs">
                    {rank}
                    <button
                      type="button"
                      className="ml-1 hover:text-destructive"
                      onClick={() => setSelectedRankings(selectedRankings.filter(r => r !== rank))}
                      aria-label={`Remove ${rank}`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
          </div>

          {/* Daily Sales Criteria Filter */}
          <div className="space-y-3">
            <Label className="text-sm font-medium">Daily Sales Criteria</Label>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  className="w-full justify-between min-h-10"
                >
                  <span className="truncate">
                    {selectedCriteria.length > 0
                      ? `${selectedCriteria.length} criteria selected`
                      : "Select criteria"}
                  </span>
                  <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-80 p-0">
                <div className="p-2 border-b">
                  <Input
                    placeholder="Search criteria..."
                    value={searchTerms.criteria}
                    onChange={(e) => setSearchTerms(prev => ({ ...prev, criteria: e.target.value }))}
                    className="h-8"
                  />
                </div>
                <div className="p-2 border-b flex gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 text-xs"
                    onClick={() => selectAll(criteriaOptions, setSelectedCriteria)}
                  >
                    Select All
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 text-xs"
                    onClick={() => clearAll(setSelectedCriteria)}
                  >
                    Clear All
                  </Button>
                </div>
                <div className="max-h-48 overflow-y-auto">
                  {getFilteredOptions(criteriaOptions, searchTerms.criteria).map(criteria => (
                    <DropdownMenuCheckboxItem
                      key={criteria}
                      checked={selectedCriteria.includes(criteria)}
                      onCheckedChange={() => toggleSelect(selectedCriteria, setSelectedCriteria, criteria)}
                      className="pl-8"
                    >
                      {criteria}
                    </DropdownMenuCheckboxItem>
                  ))}
                  {getFilteredOptions(criteriaOptions, searchTerms.criteria).length === 0 && (
                    <div className="py-2 px-4 text-sm text-muted-foreground">No criteria found</div>
                  )}
                </div>
              </DropdownMenuContent>
            </DropdownMenu>
            {selectedCriteria.length > 0 && (
              <div className="flex flex-wrap gap-1 max-h-20 overflow-y-auto">
                {selectedCriteria.map(criteria => (
                  <Badge key={criteria} variant="secondary" className="text-xs">
                    {criteria}
                    <button
                      type="button"
                      className="ml-1 hover:text-destructive"
                      onClick={() => setSelectedCriteria(selectedCriteria.filter(c => c !== criteria))}
                      aria-label={`Remove ${criteria}`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
          </div>

          {/* Window Size Input */}
          <div className="space-y-3">
            <Label htmlFor="window-size" className="text-sm font-medium">
              Window Size (days)
            </Label>
            <Input
              id="window-size"
              type="number"
              min={1}
              max={365}
              value={windowSize}
              onChange={(e) => {
                const value = Number(e.target.value);
                if (!isNaN(value) && value >= 1 && value <= 365) {
                  setWindowSize(value); // <-- updates immediately!
                }
              }}
              className="w-32"
              placeholder="Enter days"
            />
            <p className="text-xs text-muted-foreground">
              Range: 1-365 days
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 p-4 border-t border-border bg-muted/50">
          <Button
            variant="outline"
            onClick={handleReset}
            disabled={getActiveFiltersCount() === 0 && windowSize === 30}
          >
            Reset All
          </Button>
          <div className="flex gap-2">
            {onClose && (
              <Button variant="ghost" onClick={onClose}>
                Cancel
              </Button>
            )}
            {onApply && (
              <Button onClick={onApply} className="min-w-20">
                <Check className="w-4 h-4 mr-2" />
                Apply Filters
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}